import React, { useRef, useState } from 'react';
import { Upload } from 'lucide-react';

import { Container, Row, Text } from './styles';
import { useI18n } from '../../../../hooks/useI18n';
import { TranslationKeys } from '../../../../i18n/locales/defaultResources';
import Alert from '../../Alert/Alert';

interface FileUploadButtonProps {
  onFileSelected: (file: File) => void;
  isError?: boolean;
}

const FileUploadButton: React.FC<FileUploadButtonProps> = ({
  onFileSelected,
  isError = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { t } = useI18n();
  const [fileName, setFileName] = useState<string | null>(null);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertMessageType, setAlertMessageType] = useState<string>('');

  const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
  const ALLOWED_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];

  const ALLOWED_EXTESIONS = ['.pdf', '.png', '.jpg', '.jpeg'];

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const extesion = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();

    if (!ALLOWED_TYPES.includes(file.type)) {
      setIsAlertOpen(true);
      setAlertMessageType('wrongFileType');
      event.target.value = '';
      return;
    }

    if (!ALLOWED_EXTESIONS.includes(extesion)) {
      setIsAlertOpen(true);
      setAlertMessageType('wrongFileType');

      event.target.value = '';
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setIsAlertOpen(true);
      setAlertMessageType('wrongFileSize');

      event.target.value = '';
      return;
    }

    if (event.target.files && event.target.files[0]) {
      onFileSelected(event.target.files[0]);
      setFileName(event.target.files[0].name);
      event.target.value = '';
    }
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleChange}
        accept=".pdf, .png, .jpg"
      />

      <Container onClick={handleClick} $hasFile={!!fileName} $isError={isError}>
        <Upload
          size={52}
          color={fileName ? '#007ec3' : isError ? '#8D0909' : '#007ec3'}
        />
        {fileName ? (
          <Row>
            <Text>
              {t(TranslationKeys.file_upload_component_selected_file)}
            </Text>
            <Text>{': ' + fileName}</Text>
          </Row>
        ) : (
          <Text>{t(TranslationKeys.file_upload_component_upload_text)}</Text>
        )}
      </Container>
      <Alert
        messageType={alertMessageType}
        isOpen={isAlertOpen}
        onClose={() => setIsAlertOpen(false)}
      />
    </>
  );
};

export default FileUploadButton;
